import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { triggerFeedback, Feedback } from 'services/haptics.service';

/**
 * The bottom shutter bar of a scanner page: a centered round shutter
 * button plus two content-projection slots for the page's own side
 * buttons (`[left]` for upload / model selector, `[right]` for
 * Done / Analyze).
 *
 * Pressing the shutter triggers the haptic and — on web — plays the
 * shutter sound (owner decision #15: all scanners play it). On native
 * iOS the AVCapturePhotoOutput plays the system shutter sound itself,
 * so the page sets `nativeShutterSound` to skip the HTML audio and
 * avoid a double click.
 */
@Component({
  selector: 'cs-scanner-shutter-bar',
  templateUrl: 'cs-scanner-shutter-bar.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CsScannerShutterBarComponent {
  /** Hide the shutter button entirely (label scanner: camera inactive). */
  @Input() shutterHidden = false;
  /** Disable the shutter button (equipment scanner: all photos taken). */
  @Input() shutterDisabled = false;
  /** True when the native camera plays its own shutter sound. */
  @Input() nativeShutterSound = false;

  /** The shutter was pressed — page captures a photo. */
  @Output() snap = new EventEmitter<void>();

  private shutterAudio: HTMLAudioElement = null;

  onShutterClick() {
    triggerFeedback(Feedback.ImpactMedium);
    if (!this.nativeShutterSound) {
      this.playShutterSound();
    }
    this.snap.emit();
  }

  private playShutterSound() {
    try {
      if (!this.shutterAudio) {
        this.shutterAudio = new Audio('assets/sounds/camera_shutter.mp3');
      }
      this.shutterAudio.currentTime = 0;
      void this.shutterAudio.play();
    } catch { /* audio not available — capture still works, sound is a nicety */ }
  }
}
